import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { Search, ShoppingCart, CheckCircle } from 'lucide-react';
import './Marketplace.css';

const CATEGORIES = [
  { value: 'all',         label: 'All'          },
  { value: 'notes',       label: '📒 Notes'     },
  { value: 'gaming',      label: '🎮 Gaming'    },
  { value: 'clothing',    label: '👕 Clothing'  },
  { value: 'electronics', label: '💻 Electronics'},
  { value: 'books',       label: '📚 Books'     },
  { value: 'sports',      label: '⚽ Sports'    },
  { value: 'food',        label: '🍱 Food'      },
  { value: 'other',       label: '📦 Other'     },
];

const CATEGORY_LABELS = {
  notes: 'Notes', gaming: 'Gaming', clothing: 'Clothing',
  electronics: 'Electronics', books: 'Books',
  sports: 'Sports', food: 'Food', other: 'Other',
};

export default function Marketplace() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [products,   setProducts]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [category,   setCategory]   = useState('all');
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total,      setTotal]      = useState(0);
  const [cartIds,    setCartIds]    = useState(new Set());

  useEffect(() => {
    if (!user) return;
    api.get('/auth/me').then(({ data }) => {
      const ids = new Set((data.user.cart || []).map(c => c.product));
      setCartIds(ids);
    }).catch(() => {});
  }, [user]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page });
      if (category !== 'all') params.set('category', category);
      if (search.trim())      params.set('search', search.trim());
      const { data } = await api.get('/products?' + params.toString());
      setProducts(data.products);
      setTotalPages(data.pages || 1);
      setTotal(data.total || 0);
    } catch {
      toast.error('Could not load products.');
    } finally {
      setLoading(false);
    }
  }, [category, page, search]);

  useEffect(() => {
    const timer = setTimeout(fetchProducts, 300);
    return () => clearTimeout(timer);
  }, [fetchProducts]);

  const handleCategoryChange = (val) => { setCategory(val); setPage(1); };

  const handleAddToCart = async (e, productId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { navigate('/login'); return; }
    try {
      await api.post('/users/cart', { productId });
      setCartIds(prev => new Set([...prev, productId]));
      toast.success('Added to cart!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not add to cart.');
    }
  };

  return (
    <div className="marketplace fade-in">

      {/* Header */}
      <div className="market-header">
        <div>
          <h2>Marketplace</h2>
          <p className="text-muted" style={{ fontSize: '0.8rem', marginTop: 2 }}>
            {total} item{total !== 1 ? 's' : ''} available
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="market-search-wrap">
        <Search size={15} className="market-search-icon" />
        <input
          className="market-search"
          placeholder="Search products, notes, gadgets..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
      </div>

      {/* Category tabs */}
      <div className="market-cats">
        {CATEGORIES.map(cat => (
          <button
            key={cat.value}
            className={'market-cat-btn' + (category === cat.value ? ' active' : '')}
            onClick={() => handleCategoryChange(cat.value)}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="loading-center"><div className="spinner spinner-lg" /></div>
      ) : products.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🔍</div>
          <h4>No products found</h4>
          <p className="text-muted" style={{ fontSize: '0.875rem' }}>
            Try a different search or category
          </p>
        </div>
      ) : (
        <div className="market-grid">
          {products.map(product => (
            <ProductCard
              key={product._id}
              product={product}
              inCart={cartIds.has(product._id)}
              onAddToCart={handleAddToCart}
              isOwn={user && (product.seller === user._id || (product.sellerSnapshot && product.seller === user._id))}
              categoryLabel={CATEGORY_LABELS[product.category] || product.category}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="market-pagination">
          <button className="btn btn-secondary btn-sm"
            disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
          <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
            {page} / {totalPages}
          </span>
          <button className="btn btn-secondary btn-sm"
            disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}
    </div>
  );
}

function ProductCard({ product, inCart, onAddToCart, isOwn, categoryLabel }) {
  return (
    <Link to={'/app/product/' + product._id} className="product-card card card-hover">
      {/* Image */}
      <div className="product-card-img">
        {product.images && product.images[0]
          ? <img src={product.images[0]} alt={product.title} loading="lazy" />
          : <div className="product-card-no-img">📦</div>
        }
        <span className={'product-card-badge badge badge-' + product.category}>
          {categoryLabel}
        </span>
      </div>

      {/* Body */}
      <div className="product-card-body">
        <h4 className="product-card-title">{product.title}</h4>

        <div className="product-card-footer">
          <span className="product-card-price">₹{product.price.toLocaleString()}</span>
          {!isOwn && (
            <div onClick={e => e.preventDefault()}>
              <button
                className={'btn btn-sm ' + (inCart ? 'btn-success' : 'btn-secondary')}
                onClick={e => onAddToCart(e, product._id)}
                title={inCart ? 'In cart' : 'Add to cart'}
              >
                {inCart ? <CheckCircle size={13} /> : <ShoppingCart size={13} />}
                {inCart ? 'Saved' : 'Cart'}
              </button>
            </div>
          )}
        </div>

        {/* Seller row */}
        <div className="product-card-seller">
          {product.sellerSnapshot && product.sellerSnapshot.avatar
            ? <img src={product.sellerSnapshot.avatar} alt=""
                style={{ width: 18, height: 18, borderRadius: '50%', objectFit: 'cover' }} />
            : <div className="avatar-placeholder"
                style={{ width: 18, height: 18, fontSize: '0.6rem' }}>
                {product.sellerSnapshot && product.sellerSnapshot.name ? product.sellerSnapshot.name[0] : '?'}
              </div>
          }
          <span className="text-muted" style={{ fontSize: '0.72rem' }}>
            {product.sellerSnapshot ? product.sellerSnapshot.name : 'Unknown'}
          </span>
        </div>
      </div>
    </Link>
  );
}